import { useState, useRef, useEffect } from 'react';
import { Camera } from 'react-camera-pro';
import styles from './SelfieCapture.module.css';
import Button from '../Common/Button/Button';
import { useTranslation } from 'react-i18next';

export default function SelfieCapture({ onConfirm }) {
  const cameraRef = useRef(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [countdown, setCountdown] = useState(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [showCheck, setShowCheck] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const { t } = useTranslation();


  useEffect(() => {
    checkCameraPermission();
  }, []);

  const checkCameraPermission = () => {
    navigator.mediaDevices
      ?.getUserMedia({ video: true })
      .then((stream) => {
        stream.getTracks().forEach((track) => track.stop());
        setCameraReady(true);
        setCameraError(null);
      })
      .catch((err) => {
        setCameraError(err.name || 'CameraError');
      });
  };

  const capturePhoto = () => {
    setIsCapturing(true);
    let count = 3;
    setCountdown(count);
    const countdownInterval = setInterval(() => {
      count--;
      if (count > 0) setCountdown(count);
      else {
        clearInterval(countdownInterval);
        setCountdown(null);
        const image = cameraRef.current.takePhoto();
        setTimeout(() => {
          setCapturedImage(image);
          setIsCapturing(false);
        }, 300);
      }
    }, 1000);
  };

  useEffect(() => {
    if (capturedImage) setTimeout(() => setShowCheck(true), 100);
    else setShowCheck(false);
  }, [capturedImage]);

  const handleReset = () => setCapturedImage(null);
  const handleConfirm = () => onConfirm(capturedImage);
  const handleCameraError = (error) => {
    setCameraError(error?.name || 'CameraError');
  };


  if (cameraError) {
    return (
      <div className={styles.container}>
        <div className={styles.cameraContent}>
          <div className="bold font-normal mb-2" >
            {t('cameraPermissionDeniedTitle')}
          </div>
          <p className={`font-1rem ${styles.subtitle}`}>
            {t(
              'cameraPermissionDeniedDescription'
            )}
          </p>
          <Button
            text={t('retry')}
            size="normal"
            onClick={() => {
              setCameraError(null);
              checkCameraPermission();
            }}
            className="btn-primary"
          />
        </div>
      </div>
    );
  }


  if (!cameraReady && !cameraError) {
    return (
      <div className={styles.container}>
        <div className={styles.cameraContent}></div>
      </div>
    );
  }


  if (capturedImage) {
    return (
      <div className={styles.container}>
        <div className={styles.confirmContent}>
          <div className={styles.header}>
            <div className={`mt-2 ${styles.checkIcon} ${showCheck ? styles.heartbeat : ''}`}>
              <svg className={styles.checkSvg} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className={`bold font-large ${styles.title}`}>{t('selfieCapturedTitle')}</h1>
            <p className={`font-1rem ${styles.subtitle}`}>{t('selfieCapturedSubtitle')}</p>
          </div>

          <div className={styles.imagePreview}>
            <div className={styles.imageContainer}>
              <img src={capturedImage} alt="Captured selfie" className={styles.image} />
            </div>
          </div>

          <div className={styles.actions}>
            <Button
              text={t('confirm')}
              size="normal"
              onClick={handleConfirm}
              fontSize="17px"
              icon
              typeIcon="arrow"
              iconColor="#000"
              className="btn-primary"
            />
            <Button
              size="normal"
              onClick={handleReset}
              text={t('takeAnotherSelfie')}
              fontSize="17px"
              iconColor="#000"
              className="btn-primary"
              backgroundColor="#fff"
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.cameraContent}>
        <div className={styles.header}>
          <h1 className={`bold font-large ${styles.title}`}>{t('takeSelfieTitle')}</h1>
          <p className={`font-1rem ${styles.subtitle}`}>{t('takeSelfieSubtitle')}</p>
        </div>

        <div className={styles.cameraWrapper}>
          <div className={styles.cameraContainer}>
            <Camera
              ref={cameraRef}
              facingMode="user"
              aspectRatio="cover"
              onError={handleCameraError}
            />
            <div className={styles.overlay}>
              <div className={styles.faceGuide}>
                <svg viewBox="0 0 200 300" className={styles.oval}>
                  <ellipse
                    cx="100"
                    cy="150"
                    rx="90"
                    ry="140"
                    fill="none"
                    stroke="#fff"
                    strokeWidth="2"
                    strokeDasharray="8 4"
                    className={`${styles.breathing} ${styles.glow}`}
                  />
                </svg>
              </div>
            </div>
            {countdown && (
              <div className={styles.countdownOverlay}>
                <div className={styles.countdownNumber}>{countdown}</div>
              </div>
            )}
            {isCapturing && !countdown && <div className={styles.flash}></div>}
          </div>
        </div>

        <div className={styles.captureSection}>
          <p className={`font-small ${styles.hint}`}>{t('hintLighting')}</p>
          <button
            onClick={capturePhoto}
            disabled={isCapturing}
            className={`${styles.captureButton} ${styles.buttonPulse}`}
          >
            <div className={styles.captureButtonInner}></div>
          </button>
        </div>
      </div>
    </div>
  );
}
